import type { ReportLayoutConfig, SectionBuild } from '@/features/reports'

// v3 `configs/AppAnalytics/AppleSearchAds.js` (82 ln) + the render truth of
// `views/App/AppAnalytics/AppleSearchAds.vue`. Port notes:
// - v3's config file was mostly search-index metadata; the page's real panels lived in the
//   view. ORP-18952 composes those panels from the page's scoped date slot; this config keeps
//   the shared trendline/breakdown fallback and page defaults with no pending renderer.
// - The Performance Breakdown section IS ported as data: v3's 28-ln
//   `AppleSearchAdsPerformanceBreakdown.vue` wrapper was pure `o-channel-tab` + `o-data-visible`
//   (`58-performance-breakdown-${partition}`, channel 'apple') and is absorbed here, so the
//   wrapper never gets rebuilt.
// - v3 CONFIG DEFECT not ported: the config's "Search Term" sections cite
//   `mobile_searchads_keyword` (copy-paste), but the page binds the active top tab's `scTabs`
//   (`AppleSearchAds.vue:8`) — Search Term really targets `mobile_searchterm_analysis`.
//   The render truth wins (standing rule: port the design, never the defect).
// - v3 `search_value` blobs are search-index metadata with no v4 section-level home (same as
//   every 08 seed config; nav-level payloads live on `nav.ts` `meta`) — pending module 15.
// - The v3 date key suffix `'Last 28 Days'` (`AppleSearchAds.vue:69`) is the page picker
//   default → `defaultPreset`.

const ASA_REPORT_ID = 58

const KEYWORD_PARTITION = 'mobile_searchads_keyword'
const SEARCH_TERM_PARTITION = 'mobile_searchterm_analysis'

// v3 `o-data-visible :id="`58-performance-breakdown-${schedule?.partition_name}`"` — id keyed
// by the tab's first partition (the persistence key; same idiom as 08's `storeAnalysisBuild`).
const performanceBreakdownBuild: SectionBuild = ({ tab }) => ({
  id: `58-performance-breakdown-${tab.tabs?.[0] ?? tab.name}`,
  reportId: ASA_REPORT_ID,
  scheduleTabs: tab.tabs ?? [],
})

function breakdownSection(partition: string) {
  return {
    id: 'apple_search_ads__performance_breakdown',
    titleKey: 'commerceAnalytics.appleSearchAds.breakdown',
    reportId: ASA_REPORT_ID,
    build: performanceBreakdownBuild,
    // v3 rendered a single-channel rail with the Apple logo (`o-channel-tab`, channel 'apple').
    tabs: [{ name: 'Apple', channel: 'apple', tabs: [partition] }],
  }
}

function trendlineSection(partition: string) {
  return {
    id: 'apple_search_ads__performance_trendline',
    titleKey: 'commerceAnalytics.appleSearchAds.trendline',
    reportId: ASA_REPORT_ID,
    hideChannels: true,
    build: (() => ({
      id: `58-performance-trendline-${partition}`,
      reportId: ASA_REPORT_ID,
      scheduleTabs: [partition],
      view: 'chart',
      chartType: 'multi_line',
      chartDimensions: [partition === KEYWORD_PARTITION ? 'keyword' : 'search_term'],
      chartMetrics: ['app_installs', 'cost', 'cpi', 'ctr'],
    })) satisfies SectionBuild,
    tabs: [{ name: 'Performance Trendline', tabs: [partition] }],
  }
}

export const APPLE_SEARCH_ADS: ReportLayoutConfig = [
  {
    name: 'Keyword',
    labelKey: 'commerceAnalytics.appleSearchAds.tab.keyword',
    defaultPreset: 'Last 28 Days',
    sections: [trendlineSection(KEYWORD_PARTITION), breakdownSection(KEYWORD_PARTITION)],
  },
  {
    name: 'Search Term',
    labelKey: 'commerceAnalytics.appleSearchAds.tab.searchTerm',
    defaultPreset: 'Last 28 Days',
    sections: [trendlineSection(SEARCH_TERM_PARTITION), breakdownSection(SEARCH_TERM_PARTITION)],
  },
]
